import { Stage, Layer, Line, Text, Rect, Arrow, Circle } from 'react-konva';

interface Metrics {
    linearityError: number;
    motionDistance: number;
}

interface LegendProps {
    metrics?: Metrics | null;
}

const COLORS = {
    white: '#FFFFFF',
    green: '#3ec46d',
    blue: '#00B8FF',
    red: '#E15A8B',
    purple: '#A97FFF',
    orange: '#FFA500',
};
const FONT_FAMILY = "'Poppins', sans-serif";

const WIDTH = 730;
const HEIGHT = 150;
const PADDING_X = 90;
const TOP_Y = 40;
const BOTTOM_Y = 100;
const ERROR_EXAGGERATION = 1.2;

export default function SideViewLegend({ metrics }: LegendProps) {
    if (!metrics) return null;

    const { linearityError, motionDistance } = metrics;
    const optimalMotion = motionDistance - linearityError;
    const scale = motionDistance > 0 ? (WIDTH - 2 * PADDING_X) / motionDistance : 1;

    const userStart = PADDING_X;
    const optimalEnd = userStart + optimalMotion * scale;
    const GAP = 7;
    const errorStart = optimalEnd + GAP;
    const errorEnd = errorStart + linearityError * scale * ERROR_EXAGGERATION;
    const fullMotionEnd = userStart + (optimalMotion * scale + GAP + linearityError * scale * ERROR_EXAGGERATION);


    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 12,
                marginBottom: 8,
                width: WIDTH,
            }}
        >
            <Stage width={WIDTH} height={HEIGHT} style={{ backgroundColor: '#142831' }}>
                <Layer>
                    <Rect
                        x={0}
                        y={0}
                        width={WIDTH}
                        height={HEIGHT}
                        stroke={"#ccc"}
                        strokeWidth={2}
                    />

                    {/* Optimal Motion */}
                    <Circle radius={7} fill={COLORS.green} x={userStart - 10} y={TOP_Y} />
                    <Line points={[userStart, TOP_Y, optimalEnd, TOP_Y]} stroke={COLORS.blue} strokeWidth={3} />
                    <Arrow
                        points={[userStart, TOP_Y, optimalEnd, TOP_Y]}
                        stroke={COLORS.blue}
                        fill={COLORS.blue}
                        strokeWidth={3}
                        pointerLength={8}
                        pointerWidth={8}
                    />
                    <Text
                        text={`${optimalMotion.toFixed(0)} cm`}
                        x={userStart}
                        width={optimalEnd - userStart}
                        y={TOP_Y - 20}
                        align="center"
                        fill={COLORS.blue}
                        fontSize={18}
                        fontStyle="bold"
                        fontFamily={FONT_FAMILY}
                    />
                    <Text
                        text="Optimal motion"
                        x={userStart}
                        width={optimalEnd - userStart}
                        y={TOP_Y + 4}
                        align="center"
                        fill={COLORS.blue}
                        fontSize={15}
                        fontFamily={FONT_FAMILY}
                    />

                    {/* Linear Error (exaggerated) */}
                    <Arrow
                        points={[errorStart, TOP_Y, errorEnd, TOP_Y]}
                        stroke={COLORS.red}
                        fill={COLORS.red}
                        strokeWidth={3}
                        pointerLength={8}
                        pointerWidth={8}
                    />
                    <Text
                        text={`${linearityError.toFixed(0)} cm`}
                        x={errorStart}
                        width={errorEnd - errorStart}
                        y={TOP_Y - 20}
                        align="center"
                        fill={COLORS.red}
                        fontSize={18}
                        fontStyle="bold"
                        fontFamily={FONT_FAMILY}
                    />
                    <Text
                        text="Linear error"
                        x={errorStart}
                        width={errorEnd - errorStart}
                        y={TOP_Y + 4}
                        align="center"
                        fill={COLORS.red}
                        fontSize={15}
                        fontFamily={FONT_FAMILY}
                    />
                    <Circle
                        x={errorEnd + 12}
                        y={TOP_Y}
                        radius={7}
                        fill={COLORS.orange}
                    />


                    {/* User Motion */}
                    <Circle radius={7} fill={COLORS.green} x={userStart - 10} y={BOTTOM_Y} />

                    <Arrow
                        points={[userStart, BOTTOM_Y, fullMotionEnd, BOTTOM_Y]}
                        stroke={COLORS.white}
                        fill={COLORS.white}
                        strokeWidth={3}
                        pointerLength={8}
                        pointerWidth={8}
                    />
                    <Text
                        text={`${motionDistance.toFixed(0)} cm`}
                        x={userStart}
                        width={fullMotionEnd - userStart}
                        y={BOTTOM_Y - 20}
                        align="center"
                        fill={COLORS.white}
                        fontSize={18}
                        fontStyle="bold"
                        fontFamily={FONT_FAMILY}
                    />
                    <Text
                        text="User motion"
                        x={userStart}
                        width={fullMotionEnd - userStart}
                        y={BOTTOM_Y + 4}
                        align="center"
                        fill={COLORS.white}
                        fontSize={15}
                        fontFamily={FONT_FAMILY}
                    />
                    <Circle
                        x={fullMotionEnd + 12}
                        y={BOTTOM_Y}
                        radius={7}
                        fill={COLORS.orange}
                    />

                </Layer>
            </Stage>
        </div>
    );
}
